/**
 * O endereço de cada tela (#806).
 *
 * ── O que mudou ───────────────────────────────────────────────────────────
 *
 * A tela era só um estado da casca, e a URL ficava sempre em `/`: o "voltar"
 * do navegador saía do CRM, recarregar caía no Atendimento e não havia link
 * para mandar. Agora cada tela tem um caminho, e a casca e a URL andam juntas:
 * trocar de tela grava no histórico, e voltar no navegador troca a tela.
 *
 * ── Por que sem biblioteca de rotas ───────────────────────────────────────
 *
 * As telas são uma lista plana com poucos parâmetros, e a casca guarda de
 * propósito o estado entre elas (o filtro da lista de clientes sobrevive à
 * ficha). Uma biblioteca pediria para reescrever a casca em volta dela. Aqui
 * a casca continua dona do estado, e este módulo só traduz caminho ↔ tela e
 * conversa com o histórico do navegador.
 *
 * ── O que pode ir na URL ──────────────────────────────────────────────────
 *
 * Nome de tela e id, e nada mais. A URL fica no histórico do navegador, nos
 * logs do servidor e em prints de tela: nome, telefone, e-mail, documento,
 * busca ou filtro de pessoa nunca entram aqui. Id que não tem o formato de um
 * UUID nem chega à API. E a URL não dá acesso a nada: quem decide o que a
 * pessoa vê é a API (token, escopo e RLS); o que é de outra organização
 * responde 404, igual ao que não existe.
 */

import { useEffect, useRef } from 'react';
import { sairDaTela } from '@/shared/lib/guarda-de-saida';

/** As telas sem parâmetro, e o segmento de cada uma. */
const SEGMENTO_DA_TELA = {
  inbox: 'conversas',
  funil: 'funil',
  // No submenu do Funil; o caminho tem duas partes e é lido à parte, abaixo.
  'etapas-do-funil': 'funil/etapas',
  tarefas: 'tarefas',
  ia: 'ia',
  clientes: 'clientes',
  identidade: 'identidade',
  feedbacks: 'feedback',
  organizacoes: 'organizacoes',
  'super-admins': 'super-admins',
  planos: 'planos',
} as const;

export type TelaSimples = keyof typeof SEGMENTO_DA_TELA;

/**
 * As telas da área das automações (fase 2). Cada uma entra aqui junto com a
 * tela: o endereço de uma tela que não existe não é endereço de nada.
 */
export type PaginaDasAutomacoes =
  | { readonly tipo: 'lista' }
  | { readonly tipo: 'modelos' }
  /** O mapa de uma automação; com `versao`, uma versão antiga, só para ler. */
  | {
      readonly tipo: 'editor';
      readonly id: string;
      readonly versao: number | null;
      /** Acabou de ser criada do zero: o mapa abre com o recado do rascunho. Não vai para o endereço. */
      readonly novo?: boolean;
    }
  /** As listas que vêm do sistema da empresa; com `lista`, a escolhida. */
  | { readonly tipo: 'listas'; readonly lista: string | null }
  /** As conexões aos bancos dos clientes (só a Cliqfy); com `nova`, o formulário aberto. */
  | { readonly tipo: 'integracoes'; readonly nova: boolean }
  /** As operações SQL (só a Cliqfy): a lista, uma nova, ou a de `id` aberta no editor. */
  | { readonly tipo: 'operacoes'; readonly id: string | null; readonly nova: boolean }
  /** O que as automações fizeram: todas, só as que falharam, ou as de uma automação. */
  | {
      readonly tipo: 'acompanhamento';
      readonly falhou: boolean;
      readonly automacao: string | null;
    };

/** Número de versão: inteiro positivo, sem zero à esquerda. */
const VERSAO = /^[1-9]\d{0,5}$/;

export type Rota =
  | { readonly tela: 'atendimento'; readonly conversa: string | null }
  | { readonly tela: TelaSimples }
  | { readonly tela: 'cliente'; readonly id: string }
  /** Uma organização na gestão (#767), e o formulário de criar uma. */
  | { readonly tela: 'organizacao'; readonly id: string }
  | { readonly tela: 'nova-organizacao' }
  | { readonly tela: 'painel'; readonly secao: string }
  | { readonly tela: 'configuracoes'; readonly secao: string }
  | { readonly tela: 'automacoes'; readonly pagina: PaginaDasAutomacoes };

export const ROTA_INICIAL: Rota = { tela: 'atendimento', conversa: null };

/** O formato de um UUID. O que não casa nem chega à API. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Seção é um nome curto em minúsculas: o que não casa não é seção. */
const SECAO = /^[a-z][a-z0-9-]{0,39}$/;

export function ehId(valor: string): boolean {
  return UUID.test(valor);
}

export function caminhoDaRota(rota: Rota): string {
  switch (rota.tela) {
    case 'atendimento':
      return rota.conversa ? `/atendimento/conversa/${rota.conversa}` : '/atendimento';
    case 'cliente':
      return `/clientes/${rota.id}`;
    case 'organizacao':
      return `/organizacoes/${rota.id}`;
    case 'nova-organizacao':
      return '/organizacoes/nova';
    case 'painel':
      return `/relatorios/${rota.secao}`;
    case 'configuracoes':
      return `/configuracoes/${rota.secao}`;
    case 'automacoes': {
      const p = rota.pagina;
      if (p.tipo === 'modelos') return '/automacoes/modelos';
      if (p.tipo === 'integracoes') {
        return p.nova ? '/automacoes/integracoes/nova' : '/automacoes/integracoes';
      }
      if (p.tipo === 'operacoes') {
        if (p.nova) return '/automacoes/operacoes/nova';
        return p.id ? `/automacoes/operacoes/${p.id}` : '/automacoes/operacoes';
      }
      if (p.tipo === 'listas')
        return p.lista ? `/automacoes/listas/${p.lista}` : '/automacoes/listas';
      if (p.tipo === 'acompanhamento') {
        if (p.automacao) return `/automacoes/acompanhamento/${p.automacao}`;
        return p.falhou ? '/automacoes/acompanhamento/falhou' : '/automacoes/acompanhamento';
      }
      if (p.tipo === 'editor') {
        return p.versao === null ? `/automacoes/${p.id}` : `/automacoes/${p.id}/v/${p.versao}`;
      }
      return '/automacoes';
    }
    default:
      return `/${SEGMENTO_DA_TELA[rota.tela]}`;
  }
}

/**
 * A rota de um caminho, ou `null` quando ele não é de tela nenhuma.
 *
 * Só a FORMA é conferida aqui: segmento conhecido, id com formato de UUID,
 * seção com formato de seção. Se a seção existe e se a pessoa pode ver a tela
 * é a casca que decide, porque é ela que conhece o papel.
 */
export function rotaDoCaminho(caminho: string): Rota | null {
  const partes = caminho
    .split('/')
    .filter((parte) => parte !== '')
    .map((parte) => {
      try {
        return decodeURIComponent(parte);
      } catch {
        return '\u0000';
      }
    });

  if (partes.length === 0) return ROTA_INICIAL;
  const [primeira, segunda, terceira] = partes;

  if (primeira === 'atendimento') {
    if (partes.length === 1) return ROTA_INICIAL;
    if (partes.length === 3 && segunda === 'conversa' && terceira && ehId(terceira)) {
      return { tela: 'atendimento', conversa: terceira.toLowerCase() };
    }
    return null;
  }

  if (primeira === 'automacoes') {
    if (partes.length === 1) return { tela: 'automacoes', pagina: { tipo: 'lista' } };
    if (partes.length === 2 && segunda === 'modelos') {
      return { tela: 'automacoes', pagina: { tipo: 'modelos' } };
    }
    if (segunda === 'integracoes') {
      if (partes.length === 2)
        return { tela: 'automacoes', pagina: { tipo: 'integracoes', nova: false } };
      if (partes.length === 3 && partes[2] === 'nova') {
        return { tela: 'automacoes', pagina: { tipo: 'integracoes', nova: true } };
      }
      return null;
    }
    if (segunda === 'operacoes') {
      const [, , terceira] = partes;
      if (partes.length === 2) {
        return { tela: 'automacoes', pagina: { tipo: 'operacoes', id: null, nova: false } };
      }
      if (partes.length === 3 && terceira === 'nova') {
        return { tela: 'automacoes', pagina: { tipo: 'operacoes', id: null, nova: true } };
      }
      if (partes.length === 3 && terceira && ehId(terceira)) {
        return {
          tela: 'automacoes',
          pagina: { tipo: 'operacoes', id: terceira.toLowerCase(), nova: false },
        };
      }
      return null;
    }
    if (segunda === 'listas') {
      const [, , terceira] = partes;
      if (partes.length === 2)
        return { tela: 'automacoes', pagina: { tipo: 'listas', lista: null } };
      if (partes.length === 3 && terceira && ehId(terceira)) {
        return { tela: 'automacoes', pagina: { tipo: 'listas', lista: terceira.toLowerCase() } };
      }
      return null;
    }
    if (segunda === 'acompanhamento') {
      const [, , terceira] = partes;
      if (partes.length === 2) {
        return {
          tela: 'automacoes',
          pagina: { tipo: 'acompanhamento', falhou: false, automacao: null },
        };
      }
      if (partes.length === 3 && terceira === 'falhou') {
        return {
          tela: 'automacoes',
          pagina: { tipo: 'acompanhamento', falhou: true, automacao: null },
        };
      }
      if (partes.length === 3 && terceira && ehId(terceira)) {
        return {
          tela: 'automacoes',
          pagina: { tipo: 'acompanhamento', falhou: false, automacao: terceira.toLowerCase() },
        };
      }
      return null;
    }
    if (segunda && ehId(segunda)) {
      const id = segunda.toLowerCase();
      if (partes.length === 2)
        return { tela: 'automacoes', pagina: { tipo: 'editor', id, versao: null } };
      const [, , v, numero] = partes;
      if (partes.length === 4 && v === 'v' && numero && VERSAO.test(numero)) {
        return { tela: 'automacoes', pagina: { tipo: 'editor', id, versao: Number(numero) } };
      }
    }
    return null;
  }

  if (primeira === 'organizacoes' && partes.length === 2) {
    if (segunda === 'nova') return { tela: 'nova-organizacao' };
    return segunda && ehId(segunda) ? { tela: 'organizacao', id: segunda.toLowerCase() } : null;
  }

  if (primeira === 'clientes' && partes.length === 2) {
    return segunda && ehId(segunda) ? { tela: 'cliente', id: segunda.toLowerCase() } : null;
  }

  if (primeira === 'funil' && segunda === 'etapas' && partes.length === 2) {
    return { tela: 'etapas-do-funil' };
  }

  if ((primeira === 'relatorios' || primeira === 'configuracoes') && partes.length === 2) {
    if (!segunda || !SECAO.test(segunda)) return null;
    // As etapas saíram de Configurações para o Funil: o endereço antigo leva até elas.
    if (primeira === 'configuracoes' && segunda === 'etapas') return { tela: 'etapas-do-funil' };
    return primeira === 'relatorios'
      ? { tela: 'painel', secao: segunda }
      : { tela: 'configuracoes', secao: segunda };
  }

  if (partes.length === 1) {
    const tela = (Object.keys(SEGMENTO_DA_TELA) as TelaSimples[]).find(
      (chave) => SEGMENTO_DA_TELA[chave] === primeira,
    );
    if (tela) return { tela };
  }

  return null;
}

/**
 * Mudou de TELA (ou de seção), e não só de conversa?
 *
 * Trocar de tela grava uma entrada no histórico, para o "voltar" voltar a ela.
 * Trocar de conversa dentro do Atendimento só atualiza o endereço: quem passa
 * por vinte conversas numa manhã não quer apertar voltar vinte vezes para sair
 * da tela.
 */
export function mudouDeTela(antes: Rota, depois: Rota): boolean {
  if (antes.tela !== depois.tela) return true;
  if ('secao' in antes && 'secao' in depois) return antes.secao !== depois.secao;
  if (antes.tela === 'cliente' && depois.tela === 'cliente') return antes.id !== depois.id;
  if (antes.tela === 'organizacao' && depois.tela === 'organizacao') return antes.id !== depois.id;
  // Cada tela das automações é uma entrada no histórico: voltar da galeria volta à lista.
  if (antes.tela === 'automacoes' && depois.tela === 'automacoes') {
    return caminhoDaRota(antes) !== caminhoDaRota(depois);
  }
  return false;
}

/**
 * Mantém a URL e a tela da casca juntas.
 *
 * - A tela mudou: grava no histórico (`pushState`), ou só troca o endereço
 *   (`replaceState`) quando é a mesma tela, ou quando a casca acabou de abrir.
 * - O navegador voltou ou avançou: lê o caminho e entrega a rota a `aplicar`.
 *   Caminho que não é de tela vira a rota inicial, com o endereço corrigido.
 *
 * Quem decide se a pessoa pode ver a tela é a casca: `aplicar` recebe a rota
 * lida do endereço e devolve a que ela de fato abriu (a mesma, ou o
 * Atendimento quando a pessoa não pode ver aquela tela).
 */
export function useEnderecoDaTela(rota: Rota, aplicar: (rota: Rota) => Rota): void {
  const ultima = useRef<Rota | null>(null);
  const aplicarAtual = useRef(aplicar);
  aplicarAtual.current = aplicar;

  const caminho = caminhoDaRota(rota);

  useEffect(() => {
    const anterior = ultima.current;
    ultima.current = rota;
    if (window.location.pathname === caminho) return;
    // A busca (`?…`) e a âncora ficam: são de quem está no meio de um fluxo
    // (o aviso de push e a volta da Meta limpam as suas sozinhos).
    const endereco = `${caminho}${window.location.search}${window.location.hash}`;
    if (anterior === null || !mudouDeTela(anterior, rota)) {
      window.history.replaceState(null, '', endereco);
    } else {
      window.history.pushState(null, '', endereco);
    }
    // `rota` entra pelo caminho: a mesma tela com outro objeto não muda nada.
  }, [caminho]);

  useEffect(() => {
    const aoVoltar = (): void => {
      const lida = rotaDoCaminho(window.location.pathname) ?? ROTA_INICIAL;
      const seguir = (): void => {
        const aberta = aplicarAtual.current(lida);
        ultima.current = aberta;
        // Endereço que não era de tela, ou de tela que a pessoa não vê: o
        // corrigido TROCA o errado no histórico, em vez de somar uma entrada.
        const certo = caminhoDaRota(aberta);
        if (window.location.pathname !== certo) {
          window.history.replaceState(
            null,
            '',
            `${certo}${window.location.search}${window.location.hash}`,
          );
        }
      };
      // A tela aberta segurou a saída (alteração não salva): o endereço volta
      // para ela enquanto a pessoa responde, e `seguir` o troca depois.
      const atual = ultima.current;
      let seguiu = false;
      sairDaTela(() => {
        seguiu = true;
        seguir();
      });
      if (!seguiu && atual !== null) {
        window.history.pushState(
          null,
          '',
          `${caminhoDaRota(atual)}${window.location.search}${window.location.hash}`,
        );
      }
    };
    window.addEventListener('popstate', aoVoltar);
    return () => window.removeEventListener('popstate', aoVoltar);
  }, []);
}
